﻿import Phaser from 'phaser';
import { GameSoundManager } from '../audio/GameSoundManager';

/**
 * PlayerTimerRings — a single circular progress ring that moves with the
 * active player's turn.
 *
 * There is exactly ONE ring drawn at any time: the arc appears around the
 * active player's avatar and moves to the next avatar on every turn change.
 * Inactive players show no ring at all — no background track, no ghost arc.
 *
 * Implementation detail:
 *   A single Phaser.GameObjects.Graphics object (_gfx) is cleared and
 *   redrawn on every 100 ms tick. Only _pos[_activePlacement] is ever used
 *   for drawing. When the turn changes, reset() updates _activePlacement and
 *   the next _redraw() places the arc at the new avatar position.
 *
 * Timing (wall-clock):
 *   Reads Date.now() each tick — accurate under browser throttling
 *   (background tab, low battery). Prefers server turn_deadline_at when
 *   present; falls back to 30 s from the WS broadcast receipt time.
 *
 * Sound:
 *   Tick fires once per elapsed second in the final 10 s, only on the local
 *   player's own turn (placement === 'bottom').
 */

const DURATION_S   = 30;
const WARN_S       = 10;
const TICK_MS      = 100;
const RING_W       = 2.0;   // ring stroke width in px
const RING_GAP     = 1;     // gap between avatar radius edge and ring centre-line
const CLR_NORMAL   = 0xc9a84c;   // gold
const CLR_WARN     = 0xef4444;   // red
const ALPHA_NORMAL = 0.85;
const ALPHA_WARN   = 0.95;

interface RingPos {
  cx: number;
  cy: number;
  r:  number;   // avatar radius used to size the ring
}

export interface PlayerTimerRingsLayout {
  screenW:         number;
  screenH:         number;
  /** > 0 on mobile-portrait; 0 on desktop / landscape. Drives which math path. */
  sidePanelW:      number;
  topPanelH:       number;
  playAreaTopY:    number;
  playAreaBottomY: number;
  boardX:          number;
  boardY:          number;
  boardVisualSize: number;
  unit:            number;
  /** Y-centre of the local player's hand rack (used to anchor the bottom ring). */
  handY:           number;
  /** Friend-spectating (read-only): no hand rack exists, so the desktop
   *  "bottom" ring anchors mirrored off the felt edge (matching
   *  DesktopPlayerCards' panelBottom) instead of the rack-relative position.
   *  Mobile is unaffected — its bottom ring already anchors off
   *  playAreaBottomY regardless of this flag. */
  isSpectator?:    boolean;
}

export class PlayerTimerRings extends Phaser.GameObjects.Container {

  private _pos: Record<'top' | 'bottom' | 'left' | 'right', RingPos> = {
    top:    { cx: 0, cy: 0, r: 16 },
    bottom: { cx: 0, cy: 0, r: 16 },
    left:   { cx: 0, cy: 0, r: 16 },
    right:  { cx: 0, cy: 0, r: 16 },
  };

  private _activePlacement: 'top' | 'bottom' | 'left' | 'right' | null = null;
  private _deadline   = 0;   // Date.now() ms when the turn expires
  private _prevNow    = 0;
  private _running    = false;
  private _event: Phaser.Time.TimerEvent | null = null;
  private _gfx: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);
    this._gfx = scene.add.graphics();
    this.add(this._gfx);
    scene.add.existing(this);
    this.setDepth(102);   // above ScoreBoard (100), OpponentHands (46), DesktopPlayerCards (48)
    this.setVisible(false);
  }

  /**
   * Re-compute avatar centre positions for all four placements.
   * Call from applyOpponentHandsLayout() and handleResize().
   */
  setLayout(params: PlayerTimerRingsLayout): void {
    if (params.sidePanelW > 0) {
      this._computeMobilePositions(params);
    } else {
      this._computeDesktopPositions(params);
    }
    if (this._running) this._redraw();
  }

  /**
   * Start (or restart) the active seat's ring.
   * @param activeSeat   Absolute seat number (0–3) whose turn it is.
   * @param myPosition   Local player's seat number; -1 if not yet known
   *                      (or permanently, for a spectator).
   * @param deadlineMs   Date.now()-based expiry; null → 30 s from now.
   * @param isSpectator  True only for the friend-spectate screen. When
   *                      myPosition is -1 because a REAL participant's
   *                      position hasn't resolved yet, the ring stays
   *                      hidden (unchanged pre-existing behavior — avoids
   *                      flashing a ring anchored at the wrong seat). A
   *                      spectator's myPosition is -1 permanently, so it
   *                      needs its own always-on anchor (seat 0) instead —
   *                      same fixed convention as DesktopPlayerCards /
   *                      OpponentHands.
   */
  reset(activeSeat: number, myPosition: number, deadlineMs: number | null, isSpectator = false): void {
    if (myPosition >= 0 || isSpectator) {
      const anchor = myPosition >= 0 ? myPosition : 0;
      const offset = (activeSeat - anchor + 4) % 4;
      const map: Array<'bottom' | 'right' | 'top' | 'left'> =
        ['bottom', 'right', 'top', 'left'];
      this._activePlacement = map[offset];
    } else {
      this._activePlacement = null;
    }

    this._deadline = deadlineMs ?? (Date.now() + DURATION_S * 1000);
    this._prevNow  = Date.now();
    this._running  = true;

    this._event?.remove(false);
    this._event = this.scene.time.addEvent({
      delay:         TICK_MS,
      callback:      this._tick,
      callbackScope: this,
      loop:          true,
    });

    this.setVisible(true);
    this._redraw();
  }

  /** Stop all rings and hide. Call on game-over and scene cleanup. */
  stop(): void {
    this._running         = false;
    this._activePlacement = null;
    this._event?.remove(false);
    this._event = null;
    this.setVisible(false);
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private _tick(): void {
    if (!this._running) return;

    const now       = Date.now();
    const remaining = Math.max(0, (this._deadline - now) / 1000);
    const prevRem   = Math.max(0, (this._deadline - this._prevNow) / 1000);

    // Tick sound: once per elapsed second in warn window, only on my turn.
    if (
      this._activePlacement === 'bottom' &&
      remaining <= WARN_S &&
      remaining > 0 &&
      Math.ceil(prevRem) !== Math.ceil(remaining)
    ) {
      GameSoundManager.playTimerTick();
    }

    this._prevNow = now;
    this._redraw();

    if (remaining <= 0) {
      this._event?.remove(false);
      this._event = null;
      // Ring stays at 0 so all players can see the turn is overdue.
      // Frontend never auto-passes — server enforces timeout if configured.
    }
  }

  private _redraw(): void {
    this._gfx.clear();
    if (!this._activePlacement || !this._running) return;

    const pos = this._pos[this._activePlacement];
    if (pos.r <= 0) return;

    const now       = Date.now();
    const remaining = Math.max(0, (this._deadline - now) / 1000);
    const ratio     = remaining / DURATION_S;
    const isWarn    = remaining <= WARN_S;
    const fillCol   = isWarn ? CLR_WARN : CLR_NORMAL;
    const fillAlpha = isWarn ? ALPHA_WARN : ALPHA_NORMAL;
    const ringR     = pos.r + RING_GAP + RING_W / 2;

    // Single progress arc — no background track, so nothing renders for
    // inactive players. The arc drains clockwise from 12 o'clock (−π/2).
    if (ratio > 0.001) {
      const startA = -Math.PI / 2;
      const endA   = startA + ratio * Math.PI * 2;
      this._gfx.lineStyle(RING_W, fillCol, fillAlpha);
      this._gfx.beginPath();
      this._gfx.arc(pos.cx, pos.cy, ringR, startA, endA, false);
      this._gfx.strokePath();
    }
  }

  // ─── Layout math (mirrors OpponentHands geometry) ────────────────────────

  private _computeMobilePositions(p: PlayerTimerRingsLayout): void {
    const { screenW, sidePanelW: spw, topPanelH, playAreaTopY, playAreaBottomY } = p;
    const MARGIN      = 16;
    const SIDE_MARGIN = 8;

    // Horizontal panels (top = partner, bottom = me).
    // Avatar geometry mirrors OpponentHands.drawTopPanelContent so the ring sits
    // on the exact avatar centre. Cap is intentionally smaller than OpponentHands'
    // avatarR cap (20) — produces a smaller badge-style ring overlaid on the avatar.
    const panHH  = Math.round(topPanelH * 0.90);
    const avRH   = Math.min(Math.round(panHH * 0.28), 12);
    const avCX_H = MARGIN + spw / 2;   // avatar X: left column, same as OpponentHands

    this._pos.top = {
      cx: avCX_H,
      cy: (playAreaTopY - panHH - 4) + Math.round(panHH * 0.36),
      r:  avRH,
    };

    // Bottom = local player. Anchored on the bottom-panel avatar drawn by
    // OpponentHands (panY = playAreaBottomY + 4, avatarY = panY + round(panH * 0.36)).
    // avCX_H (≈49 px from left) sits to the LEFT of all rack tiles (which start
    // at ≈75–80 px from left), so the ring never overlaps tile images even though
    // the Y coordinate falls inside the rack zone.
    this._pos.bottom = {
      cx: avCX_H,
      cy: (playAreaBottomY + 4) + Math.round(panHH * 0.36),
      r:  avRH,
    };

    // Vertical panels (left, right) — avatar geometry mirrors
    // OpponentHands.drawSidePanelContent exactly.
    const panWS   = Math.round(spw * 0.715);
    const playH   = playAreaBottomY - playAreaTopY;
    const sideH   = Math.min(screenW, playH);
    const panHS   = Math.min(Math.round(sideH * 0.95), 286);
    const centerY = (playAreaTopY + playAreaBottomY) / 2;
    const panYS   = centerY - panHS / 2;
    const avRS    = Math.max(10, Math.min(Math.round(panWS * 0.32), 14));
    const avYS    = panYS + 7 + avRS;

    this._pos.left  = { cx: SIDE_MARGIN + panWS / 2,               cy: avYS, r: avRS };
    this._pos.right = { cx: screenW - SIDE_MARGIN - panWS / 2,     cy: avYS, r: avRS };
  }

  private _computeDesktopPositions(p: PlayerTimerRingsLayout): void {
    const { boardX, boardY, boardVisualSize, unit, handY, isSpectator } = p;
    const avR   = Math.max(8, Math.round(unit * 0.26));
    const inset = avR + RING_GAP + RING_W / 2 + 4;
    const halfB = boardVisualSize / 2;

    // Top / left / right: ring just inside the felt edge, in front of the
    // opponent card that sits on the wooden rail outside the felt.
    this._pos.top   = { cx: boardX,               cy: boardY - halfB + inset, r: avR };
    this._pos.left  = { cx: boardX - halfB + inset, cy: boardY,               r: avR };
    this._pos.right = { cx: boardX + halfB - inset, cy: boardY,               r: avR };

    if (isSpectator) {
      // Spectating: no hand rack exists to anchor off — mirror 'top' across
      // the board center instead, landing just inside the bottom felt edge
      // (matching DesktopPlayerCards.panelBottom, the same mirrored panel).
      this._pos.bottom = { cx: boardX, cy: boardY + halfB - inset, r: avR };
      return;
    }

    // Bottom = local player. No player card exists on desktop — the rack IS
    // the player's identity. Place ring just above the rack background so it
    // is clearly in "my zone" without sitting on top of any tile.
    // Desktop handScale = 1.389 (GameScene desktop constant); tileH = unit * 2 * 1.389.
    // rackPadH = 8 — keep in sync with Hand.ts rackPadH.
    const DESKTOP_HS = 1.389;
    const tileHalfH  = Math.round(unit * DESKTOP_HS);   // = tileH / 2
    const RACK_PAD_H = 8;
    const rackTopY   = handY - tileHalfH - RACK_PAD_H;
    this._pos.bottom = {
      cx: boardX,
      cy: rackTopY - avR - RING_GAP - RING_W / 2 - 4,
      r:  avR,
    };
  }
}
